import clsx from 'clsx';
import * as React from 'react';
import type { SeparatorProps as RACSeparatorProps } from 'react-aria-components';
import { Separator as RACSeparator } from 'react-aria-components';

interface DividerProps {
    label?: string;
    /** @default "horizontal" */
    orientation?: 'horizontal' | 'vertical';
    /** @default "center" */
    alignment?: 'left' | 'center' | 'right';
    className?: string;
}

/**
 * Accessible separator primitive used by Divider.
 */
export function Separator({
    ref,
    ...props
}: RACSeparatorProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    return (
        <RACSeparator
            ref={ref}
            {...props}
            className={clsx(
                props.orientation === 'vertical' ? 'h-full border-l' : 'w-full border-t',
                props.className
            )}
        />
    );
}

/**
 * Visual divider that can optionally render a label over the separator line.
 */
export function Divider(props: DividerProps) {
    const orientation = props.orientation || 'horizontal';
    const isVertical = orientation === 'vertical';
    const alignment = props.alignment || 'center';

    const labelAlignmentClass = isVertical
        ? {
              left: 'justify-self-center self-start',
              center: 'place-self-center',
              right: 'justify-self-center self-end',
          }[alignment]
        : {
              left: 'justify-self-start self-center',
              center: 'place-self-center',
              right: 'justify-self-end self-center',
          }[alignment];
    const labelPaddingClass = isVertical
        ? {
              left: 'px-0 pb-2',
              center: 'px-0 py-2',
              right: 'px-0 pt-2',
          }[alignment]
        : {
              left: 'pr-2 py-0',
              center: 'px-2 py-0',
              right: 'pl-2 py-0',
          }[alignment];

    return (
        <div
            className={clsx(
                'grid [grid-template-columns:1fr] [grid-template-rows:1fr]',
                isVertical ? 'mx-0.5 min-h-0 min-w-px self-stretch' : 'my-0.5 w-full',
                props.className
            )}
        >
            <Separator
                orientation={orientation}
                className={clsx(
                    'place-self-center [grid-area:1/1]',
                    isVertical ? 'mx-0 h-full w-px' : 'my-0 h-px w-full'
                )}
            />

            {props.label ? (
                <span
                    className={clsx(
                        'z-[1] bg-base text-ui-heading-group text-muted [grid-area:1/1] [text-box:trim-both_cap_alphabetic]',
                        labelAlignmentClass,
                        labelPaddingClass
                    )}
                >
                    {isVertical ? (
                        <span className="flex flex-col items-center leading-none">
                            {splitUserPerceivedChars(props.label).map((char, i) => (
                                <span key={i}>{char}</span>
                            ))}
                        </span>
                    ) : (
                        props.label
                    )}
                </span>
            ) : null}
        </div>
    );
}

/**
 * Splits a string into an array of grapheme clusters (user-perceived characters).
 *
 * This ensures complex characters (ie: emojis, ZWJ - Zero Width Joiner - emoji sequences,
 * characters with diacritics) are handled correctly.
 * It uses the Intl.Segmenter API if available and fallbacks to codepoint iteration.
 */
function splitUserPerceivedChars(input: string) {
    if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
        const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
        return Array.from(segmenter.segment(input), (s) => s.segment);
    }

    // Fallback to codepoint iteration (not perfect for ZWJ sequences)
    return Array.from(input);
}
